import type {
  AuditEventRecord,
  AuditListResponse,
  WorkspaceConnectionsResponse,
  WorkspaceOperator,
} from "@carbon-operations/contracts";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { stateLabel } from "./run-list-view.js";
import { usageLabel, verificationLabel } from "./workspace-records.js";
import "./workspace-records.css";

// Every value is rendered as React text (escaped). None of these views offers a control that changes state.

function Fact({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="definition">
      <dt>{term}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function StatusChip({ tone, children }: { tone: "neutral" | "success" | "attention" | "danger"; children: ReactNode }) {
  return <span className={`status ${tone}`}><i />{children}</span>;
}

function when(iso: string) {
  return <time dateTime={iso}>{new Date(iso).toLocaleString()}</time>;
}

function initials(value: string) {
  return value.split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase()).join("") || "OP";
}

export function OperatorDirectory({ operators }: { operators: WorkspaceOperator[] }) {
  if (operators.length === 0) return <p className="empty-state">No memberships are recorded for this workspace.</p>;
  return (
    <div className="records-grid">
      {operators.map((operator) => (
        <article className="operator-card record-card" key={operator.operator_id}>
          <span className="large-avatar" aria-hidden="true">{initials(operator.display_name)}</span>
          <div>
            <small>{operator.operator_type} · {operator.role}</small>
            <h2>{operator.display_name}</h2>
            <code className="record-id">{operator.operator_id}</code>
          </div>
          <dl className="definitions record-facts">
            <Fact term="Membership">
              <StatusChip tone={operator.membership_status === "active" ? "success" : "danger"}>
                {stateLabel(operator.membership_status)}
              </StatusChip>{" "}
              <span className="record-note">since {when(operator.member_since)}</span>
            </Fact>
            <Fact term="Operator">
              <StatusChip tone={operator.operator_status === "active" ? "success" : "danger"}>
                {stateLabel(operator.operator_status)}
              </StatusChip>
            </Fact>
            <Fact term="Scopes">
              {operator.scopes.length === 0
                ? "None"
                : <span className="chip-list">{operator.scopes.map((scope) => <code key={scope}>{scope}</code>)}</span>}
            </Fact>
            <Fact term="Capability grants">
              {operator.capabilities.length === 0
                ? "None"
                : (
                  <ul className="record-list">
                    {operator.capabilities.map((grant) => (
                      <li key={`${grant.capability}@${grant.version}`}>
                        <code>{grant.capability} v{grant.version}</code> {grant.title}
                        <span className="record-note">granted by {grant.granted_by} · {when(grant.granted_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
            </Fact>
          </dl>
        </article>
      ))}
    </div>
  );
}

type Connections = Pick<WorkspaceConnectionsResponse, "event_sinks" | "object_store_profiles" | "secret_refs">;

export function ConnectionRecords({ connections }: { connections: Connections }) {
  return (
    <div className="records-stack">
      <section aria-label="Event sinks">
        <h2 className="records-heading">Event sinks</h2>
        {connections.event_sinks.length === 0
          ? <p className="empty-state">No event sink is recorded for this workspace.</p>
          : (
            <div className="records-grid">
              {connections.event_sinks.map((sink) => (
                <article className="connection-card record-card" key={sink.event_sink_id}>
                  <header>
                    <div>
                      <small>Event sink · version {sink.sink_version}</small>
                      <h2>{sink.event_sink_id}</h2>
                    </div>
                    <StatusChip tone={sink.status === "active" ? "success" : "neutral"}>{stateLabel(sink.status)}</StatusChip>
                  </header>
                  <dl className="definitions">
                    <Fact term="Destination origin">{sink.destination_origin}</Fact>
                    <Fact term="Signing key ID">{sink.key_id}</Fact>
                    <Fact term="Signing key reference">{sink.signing_key_ref_id ?? "None"}</Fact>
                    <Fact term="Created">{when(sink.created_at)}</Fact>
                  </dl>
                </article>
              ))}
            </div>
          )}
      </section>
      <section aria-label="Object store profiles">
        <h2 className="records-heading">Object store profiles</h2>
        {connections.object_store_profiles.length === 0
          ? <p className="empty-state">No object store profile is recorded for this workspace.</p>
          : (
            <div className="records-grid">
              {connections.object_store_profiles.map((profile) => (
                <article className="connection-card record-card" key={`${profile.profile_id}@${profile.profile_version}`}>
                  <header>
                    <div>
                      <small>{profile.provider} · version {profile.profile_version}</small>
                      <h2>{profile.profile_id}</h2>
                    </div>
                    <StatusChip tone={profile.status === "active" ? "success" : "neutral"}>{stateLabel(profile.status)}</StatusChip>
                  </header>
                  <dl className="definitions">
                    <Fact term="Bucket reference">{profile.bucket_ref}</Fact>
                    <Fact term="Credential reference">{profile.credential_ref_id ?? "None"}</Fact>
                    <Fact term="Configuration SHA-256"><code className="record-digest">{profile.configuration_sha256}</code></Fact>
                    <Fact term="Created">{when(profile.created_at)}</Fact>
                  </dl>
                </article>
              ))}
            </div>
          )}
      </section>
      <section aria-label="Secret references">
        <h2 className="records-heading">Secret references</h2>
        {connections.secret_refs.length === 0
          ? <p className="empty-state">No secret reference is used by this workspace's records.</p>
          : (
            <div className="records-grid">
              {connections.secret_refs.map((ref) => (
                <article className="connection-card record-card" key={ref.secret_ref_id}>
                  <header>
                    <div>
                      <small>{ref.provider}</small>
                      <h2>{ref.secret_ref_id}</h2>
                    </div>
                    <StatusChip tone={ref.status === "active" ? "success" : "danger"}>{stateLabel(ref.status)}</StatusChip>
                  </header>
                  <dl className="definitions">
                    <Fact term="Purpose">{ref.purpose}</Fact>
                    <Fact term="Used for">{ref.usages.map(usageLabel).join(" · ")}</Fact>
                    <Fact term="Created">{when(ref.created_at)}</Fact>
                    <Fact term="Revoked">{ref.revoked_at === undefined ? "Not revoked" : when(ref.revoked_at)}</Fact>
                  </dl>
                </article>
              ))}
            </div>
          )}
      </section>
    </div>
  );
}

export function ChainStatus({ chain }: { chain: AuditListResponse["chain"] }) {
  return (
    <div className={`chain-status ${chain.head_matches ? "verified" : "broken"}`} role="status">
      <StatusChip tone={chain.head_matches ? "success" : "danger"}>
        {chain.head_matches ? "Chain head verified" : "Chain head mismatch"}
      </StatusChip>
      <span>Tenant chain position {chain.head_sequence}</span>
      {chain.head_sha256 !== undefined && <code className="record-digest">{chain.head_sha256}</code>}
    </div>
  );
}

export function AuditLedger({ events, base }: { events: AuditEventRecord[]; base: string }) {
  if (events.length === 0) return <p className="empty-state">No audit events match.</p>;
  return (
    <ol className="ledger" aria-label="Audit events">
      {events.map((event) => {
        const verified = verificationLabel(event.verification);
        return (
          <li className="ledger-row" key={event.audit_sequence}>
            <span className="ledger-position">#{event.audit_sequence}</span>
            <div className="ledger-main">
              <strong>{event.event_type}</strong>
              <span className="record-note">{when(event.occurred_at)}</span>
              <span className="record-note">
                {event.subject_type} <code>{event.subject_id}</code> · {event.actor_type}
                {event.actor_id === undefined ? "" : ` ${event.actor_id}`}
              </span>
              {event.run_id !== undefined && (
                <Link className="strong-link" to={`${base}/runs/${encodeURIComponent(event.run_id)}`}>{event.run_id}</Link>
              )}
            </div>
            <div className="ledger-verification">
              <StatusChip tone={verified.tone}>{verified.text}</StatusChip>
              <code className="record-digest" title="Event SHA-256">{event.event_sha256}</code>
            </div>
            <details className="ledger-payload">
              <summary>Payload</summary>
              <pre>{JSON.stringify(event.payload, null, 2)}</pre>
            </details>
          </li>
        );
      })}
    </ol>
  );
}
